/**
 * SampleDetectionBanner — onboarding "aha moment" widget.
 *
 * Renders a single mock detection card on the dashboard for new orgs that
 * have created a canary but have not yet recorded any real detections. The
 * banner is purely client-side: no fake rows are written to D1 (which would
 * confuse API consumers and platform admins). Dismissal is per-org via
 * localStorage.
 */

import { useCallback, useState } from 'react'
import { ConfidenceBadge } from '@/components/ui'

type SampleDetectionBannerProps = {
  organizationId: string
  onDismiss?: () => void
}

const SAMPLE_FAKE_FQDN = 'fake-yoursite.phishing-example.com'
const SAMPLE_CONFIDENCE = 87

export function dismissalKey(orgId: string): string {
  return `spoofio:sample-dismissed:${orgId}`
}

export function isSampleDismissed(orgId: string): boolean {
  try {
    return window.localStorage.getItem(dismissalKey(orgId)) === '1'
  } catch {
    return false
  }
}

export function SampleDetectionBanner({
  organizationId,
  onDismiss,
}: SampleDetectionBannerProps) {
  const [hidden, setHidden] = useState<boolean>(() => isSampleDismissed(organizationId))

  const handleDismiss = useCallback(() => {
    try {
      window.localStorage.setItem(dismissalKey(organizationId), '1')
    } catch {
      // ignore — best effort
    }
    setHidden(true)
    onDismiss?.()
  }, [organizationId, onDismiss])

  if (hidden) return null

  return (
    <div
      data-testid="sample-detection-banner"
      className="rounded-lg border border-primary-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-2 bg-primary-100 text-primary-800 text-xs font-semibold uppercase tracking-wide">
        <span>SAMPLE — This is what a real detection looks like</span>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-primary-700 hover:text-primary-900 font-medium normal-case text-sm"
        >
          Dismiss
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 text-sm">
        <div>
          <div className="text-gray-500 text-xs">Fake Domain</div>
          <div className="font-mono">{SAMPLE_FAKE_FQDN}</div>
        </div>
        <div>
          <div className="text-gray-500 text-xs">Confidence</div>
          <ConfidenceBadge score={SAMPLE_CONFIDENCE} />
        </div>
        <div>
          <div className="text-gray-500 text-xs">Detected</div>
          <div>2 minutes ago</div>
        </div>
      </div>
    </div>
  )
}
